import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createLoyaltySegment,
  deleteLoyaltySegment,
  getCustomerLoyaltySegments,
  getLoyaltySegmentMembers,
  getLoyaltySegments,
  previewLoyaltySegment,
  updateLoyaltySegment,
} from '../../../../gateways/breadstack/loyaltySegmentApis'
import type {
  CreateSegmentInput,
  LoyaltySegmentsList,
  SegmentCriteria,
  SegmentMembersSort,
  UpdateSegmentInput,
} from '../../../../types/loyaltySegments'

// Query key — the storeId lives in the key so every store's segments cache
// independently and invalidation is scoped per store (same convention as
// useLoyaltyMarketing).
export const loyaltySegmentsQueryKey = (storeId: string) =>
  ['loyalty', 'segments', storeId] as const

// The full list payload (segments + the distinct `customers_covered` count).
// Only the Segments tab's stats strip needs the list-level number.
export const useLoyaltySegmentsListQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltySegmentsQueryKey(storeId),
    queryFn: () => getLoyaltySegments(storeId),
    enabled: Boolean(storeId),
  })

// Array-shaped view of the same query — every other consumer (tab badge,
// campaign builder's segment picker, segment builder's edit lookup) only wants
// the segments, so `select` keeps their `data` a plain LoyaltySegment[] while
// sharing one cache entry with the list query above.
export const useLoyaltySegmentsQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltySegmentsQueryKey(storeId),
    queryFn: () => getLoyaltySegments(storeId),
    enabled: Boolean(storeId),
    select: (list: LoyaltySegmentsList) => list.segments,
  })

// The segments one customer belongs to (customer-detail pills). Disabled
// until both ids are known; POS-only customers with no Woo id get none.
export const useCustomerSegmentsQuery = (
  storeId: string,
  customerId: string | number | undefined,
) =>
  useQuery({
    queryKey: ['loyalty', 'customer-segments', storeId, String(customerId ?? '')],
    queryFn: () => getCustomerLoyaltySegments(storeId, customerId as string | number),
    enabled: Boolean(storeId) && customerId != null && String(customerId).trim() !== '',
    staleTime: 60 * 1000,
  })

export const useCreateLoyaltySegment = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSegmentInput) =>
      createLoyaltySegment(storeId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltySegmentsQueryKey(storeId),
      }),
  })
}

export const useUpdateLoyaltySegment = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      segmentId,
      input,
    }: {
      segmentId: string
      input: UpdateSegmentInput
    }) => updateLoyaltySegment(storeId, segmentId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltySegmentsQueryKey(storeId),
      }),
  })
}

export const useDeleteLoyaltySegment = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (segmentId: string) => deleteLoyaltySegment(storeId, segmentId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltySegmentsQueryKey(storeId),
      }),
  })
}

// Preview is a pure server evaluation — nothing persisted, no cache to
// invalidate. The builder dialog debounces the criteria snapshot and reads
// `{count, sample}` off the resolved value.
export const useSegmentPreviewMutation = () =>
  useMutation({
    mutationFn: ({
      storeId,
      criteria,
    }: {
      storeId: string
      criteria: SegmentCriteria
    }) => previewLoyaltySegment(storeId, criteria),
  })

// ── Segment members (detail page table) ──────────────────────────────────────

export interface UseSegmentMembersQueryOptions {
  sort?: SegmentMembersSort
  q?: string
  cursor?: number | string
  limit?: number
}

// `limit` deliberately stays out of the key — it's the page size, constant
// for a given table, not a filter the cache needs to branch on.
export const segmentMembersQueryKey = (
  storeId: string,
  segmentId: string,
  sort: SegmentMembersSort,
  q: string,
  cursor: number | string | undefined,
) =>
  ['loyalty', 'segment-members', storeId, segmentId, sort, q, cursor] as const

// Paginated (offset cursor), sorted, searchable member list backing the
// segment detail page's customer table. `keepPreviousData` so paging/sorting
// doesn't flash the table empty between requests.
export const useSegmentMembersQuery = (
  storeId: string,
  segmentId: string,
  segmentExists: boolean,
  { sort = 'ltv', q = '', cursor, limit }: UseSegmentMembersQueryOptions = {},
) =>
  useQuery({
    queryKey: segmentMembersQueryKey(storeId, segmentId, sort, q, cursor),
    queryFn: () =>
      getLoyaltySegmentMembers(storeId, segmentId, { limit, cursor, sort, q }),
    // Don't fire for a segment id the (cached) list doesn't recognize — an
    // unknown/deleted id, or the list simply hasn't loaded yet.
    enabled: Boolean(storeId) && Boolean(segmentId) && segmentExists,
    placeholderData: keepPreviousData,
  })
